// changed by Cursor: icon practice = button buat akses 3 practice; 3 practice tidak di dalam lesson
// changed by Cursor: lesson pake numbered circle (123), skill pake alphabet circle (abc)
import React, { useState, useEffect, useRef } from 'react';
import { CaretRight, CaretDown, CircleNotch, Lightning, Microphone, PencilSimple } from 'phosphor-react';

const getAlphabetLabel = (index) => {
  if (index < 26) return String.fromCharCode(97 + index);
  const first = Math.floor(index / 26) - 1;
  const second = index % 26;
  return String.fromCharCode(97 + first) + String.fromCharCode(97 + second);
};

const MobileLessonsTree = ({
  lessons = [],
  skillsCache = {},
  expandedLessonIds = new Set(),
  loadingLessonId = null,
  practices = { quick: {}, writing: {}, speaking: {} },
  loadingStats = false,
  onToggleLesson,
  onSkillClick,
  onQuickPracticeStart,
  onWritingPracticeStart,
  onSpeakingPracticeStart,
}) => {
  const [practiceOpenLessonId, setPracticeOpenLessonId] = useState(null);
  const practiceDropdownRef = useRef(null);

  // changed by Cursor: close practice dropdown when clicking outside, scrolling, or touch-moving
  useEffect(() => {
    if (practiceOpenLessonId === null) return;
    const handleClickOutside = (e) => {
      if (practiceDropdownRef.current && !practiceDropdownRef.current.contains(e.target)) {
        setPracticeOpenLessonId(null);
      }
    };
    const handleDismiss = () => setPracticeOpenLessonId(null);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    window.addEventListener('scroll', handleDismiss, { capture: true, passive: true });
    document.addEventListener('scroll', handleDismiss, { capture: true, passive: true });
    document.addEventListener('touchmove', handleDismiss, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      window.removeEventListener('scroll', handleDismiss, { capture: true });
      document.removeEventListener('scroll', handleDismiss, { capture: true });
      document.removeEventListener('touchmove', handleDismiss);
    };
  }, [practiceOpenLessonId]);

  const renderPracticeBlock = (lesson) => {
    const lessonId = lesson.id || lesson._id;
    const q = lesson['quick-completion'] ?? null;
    const w = lesson['writing-completion'] ?? null;
    const s = lesson['speaking-completion'] ?? null;
    const quickProgress = loadingStats ? 0 : (q !== null ? Math.min(100, Math.max(0, q)) : (practices.quick?.progress ?? 0));
    const writingProgress = loadingStats ? 0 : (w !== null ? Math.min(100, Math.max(0, w)) : (practices.writing?.progress ?? 0));
    const speakingProgress = loadingStats ? 0 : (s !== null ? Math.min(100, Math.max(0, s)) : (practices.speaking?.progress ?? 0));
    return (
      <div className="absolute top-full right-0 mt-1 min-w-[260px] max-w-[calc(100vw-24px)] p-[10px_12px] bg-[var(--bg-secondary,#fff)] dark:bg-[var(--card-bg)] border border-[rgba(0,0,0,0.08)] dark:border-[var(--border-color)] rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3)] z-[100]">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[13px] font-semibold text-[rgba(26,26,46,0.85)] dark:text-[var(--text-primary)] m-0 p-0">Practice in this material</p>
        </div>
        <div className="flex flex-col gap-1.5">
          {[
            { icon: <Lightning size={20} weight="regular" />, name: 'Quick Practice', progress: quickProgress, onStart: () => onQuickPracticeStart?.({ lessonId }) },
            { icon: <PencilSimple size={20} weight="regular" />, name: 'Writing', progress: writingProgress, onStart: () => onWritingPracticeStart?.({ lessonId }) },
            { icon: <Microphone size={20} weight="regular" />, name: 'Speaking', progress: speakingProgress, onStart: () => onSpeakingPracticeStart?.({ lessonId }) },
          ].map(({ icon, name, progress, onStart }) => (
            <div key={name} className="flex flex-wrap items-center gap-2 p-[8px_10px] bg-[rgba(18,118,109,0.04)] dark:bg-[var(--accent-teal-light)] rounded-md">
              <span className="flex-shrink-0 text-[#12766d] dark:text-[var(--accent-teal)]">{icon}</span>
              <span className="flex-1 min-w-0 text-[13px] font-medium text-[rgba(26,26,46,0.85)] dark:text-[var(--text-primary)]">{name}</span>
              <button
                type="button"
                className="mobile-lessons-tree-practice-btn flex-shrink-0 py-[6px] px-3 bg-gradient-to-br from-[#fcb319] to-[#f5a500] dark:from-[#fbbf24] dark:to-[#f59e0b] text-[#1d4746] border-none rounded-md text-[12px] font-semibold cursor-pointer shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:from-[#f5a500] hover:to-[#e69500] active:opacity-90"
                onClick={onStart}
              >
                Start
              </button>
              <div className="flex items-center gap-2 w-full mt-1">
                <div className="flex-1 h-1 bg-[rgba(18,118,109,0.12)] dark:bg-[rgba(20,184,166,0.12)] rounded-sm overflow-hidden">
                  <div className="mobile-lessons-tree-practice-fill h-full bg-[#12766d] dark:bg-[var(--accent-teal)] rounded-sm transition-[width_0.2s_ease]" style={{ width: `${progress}%` }} />
                </div>
                <span className="mobile-lessons-tree-practice-pct text-[11px] font-semibold text-[#12766d] dark:text-[var(--accent-teal)] min-w-[22px]">
                  {loadingStats ? '--' : `${Math.round(progress)}%`}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="mobile-lessons-tree px-3 pb-6 mt-3">
      <div className="text-[15px] font-semibold text-[rgba(26,26,46,0.9)] dark:text-[var(--text-primary)] pb-[10px]">Lessons</div>
      <ul className="list-none p-0 m-0" role="tree">
        {lessons.map((lesson) => {
          const lessonId = lesson.id || lesson._id;
          const title = lesson.title || lesson.name || `Lesson ${lessonId}`;
          const isExpanded = expandedLessonIds.has(lessonId);
          const cached = skillsCache[lessonId];
          const skills = cached?.skills || [];
          const loading = loadingLessonId === lessonId;

          const q = lesson['quick-completion'] ?? null;
          const w = lesson['writing-completion'] ?? null;
          const s = lesson['speaking-completion'] ?? null;
          const all = lesson['all-completion'] ?? null;
          const progressPct = all !== null ? all : (q !== null || w !== null || s !== null)
            ? Math.round(((q ?? 0) + (w ?? 0) + (s ?? 0)) / 3)
            : null;
          // changed by Cursor: mark lesson as completed when progress >= 100
          const isCompleted = progressPct !== null && progressPct >= 100;
          // changed by Cursor: high progress when >= 80% but not yet completed
          const isHighProgress = progressPct !== null && progressPct >= 80 && !isCompleted;

          return (
            <li
              key={lessonId}
              className={`mobile-lessons-tree-lesson mb-2 p-0${isCompleted ? ' lesson-completed' : ''}${isHighProgress ? ' lesson-high-progress' : ''}`}
              role="treeitem"
              aria-expanded={isExpanded}
              data-lesson-id={lessonId}
            >
              <div className="mobile-lessons-tree-lesson-block relative p-2 rounded-[10px] border-[1.5px] border-transparent transition-[background_0.2s_ease,border-color_0.2s_ease]">
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    className="mobile-lessons-tree-expand-btn flex items-center justify-center flex-shrink-0 w-6 h-6 p-0 bg-none border-none rounded-[4px] cursor-pointer text-[var(--text-secondary,rgba(26,26,46,0.5))] self-center transition-colors duration-200 hover:text-[var(--text-primary,rgba(26,26,46,0.9))]"
                    onClick={() => onToggleLesson(lessonId)}
                    aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
                  >
                    {isExpanded ? (
                      <CaretDown size={14} weight="bold" />
                    ) : (
                      <CaretRight size={14} weight="bold" />
                    )}
                  </button>
                  <div className="mobile-lessons-tree-number w-[26px] h-[26px] rounded-full bg-[var(--progress-bg,rgba(18,118,109,0.08))] text-[var(--text-primary,rgba(26,26,46,0.9))] flex items-center justify-center text-[12px] font-semibold flex-shrink-0 border-2 border-[var(--text-tertiary,rgba(26,26,46,0.3))] self-center">
                    {lesson.order ?? lessonId}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-2 py-0.5 min-h-6 w-full">
                      {/* changed by Cursor: klik judul = expand + buka skill pertama */}
                      <span
                        className="mobile-lessons-tree-lesson-title flex-1 min-w-0 text-[15px] font-medium text-[rgba(26,26,46,0.9)] dark:text-[var(--text-primary)] break-words cursor-pointer"
                        onClick={() => {
                          if (!isExpanded) onToggleLesson(lessonId);
                          const cachedSkills = skillsCache[lessonId]?.skills || [];
                          if (cachedSkills.length > 0) {
                            const firstSkillId = cachedSkills[0].id || cachedSkills[0]._id;
                            onSkillClick(firstSkillId, lessonId);
                          }
                        }}
                      >{title}</span>
                      {loading && (
                        <span className="flex items-center flex-shrink-0 text-[#12766d] dark:text-[var(--accent-teal)]">
                          <CircleNotch size={16} weight="bold" className="animate-mobile-spin" />
                        </span>
                      )}
                    </div>
                    {progressPct !== null && (
                      <div className="flex items-center gap-2 w-full mt-1.5">
                        <div className="mobile-lessons-tree-progress-bar flex-1 h-1 bg-[rgba(18,118,109,0.15)] dark:bg-[rgba(20,184,166,0.15)] rounded-sm overflow-hidden">
                          <div
                            className="mobile-lessons-tree-progress-fill h-full bg-[#12766d] dark:bg-[var(--accent-teal)] rounded-sm transition-[width_0.2s_ease]"
                            style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
                          />
                        </div>
                        <span className="mobile-lessons-tree-progress-text text-[11px] font-semibold text-[#12766d] dark:text-[var(--accent-teal)] min-w-[24px]">
                          {Math.round(progressPct)}%
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="relative flex-shrink-0" ref={practiceOpenLessonId === lessonId ? practiceDropdownRef : undefined}>
                    <button
                      type="button"
                      className={`mobile-lessons-tree-practice-trigger inline-flex items-center justify-center flex-shrink-0 py-[6px] px-3 bg-gradient-to-br from-[#fcb319] to-[#f5a500] dark:from-[#fbbf24] dark:to-[#f59e0b] text-[#1d4746] border-none rounded-md text-[13px] font-semibold cursor-pointer shadow-[0_2px_4px_rgba(252,179,25,0.2)] hover:from-[#f5a500] hover:to-[#e69500] ${practiceOpenLessonId === lessonId ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPracticeOpenLessonId((prev) => (prev === lessonId ? null : lessonId));
                      }}
                      aria-label={practiceOpenLessonId === lessonId ? 'Close practice' : `Practice for ${title}`}
                    >
                      Practice
                    </button>
                    {practiceOpenLessonId === lessonId && renderPracticeBlock(lesson)}
                  </div>
                </div>
                {isExpanded && (
                  <ul className="mobile-lessons-tree-skills list-none p-0 pl-7 mt-1 m-0 relative" role="group">
                    {loading && skills.length === 0 ? (
                      <li className="mobile-lessons-tree-skill-item relative py-[6px] pl-[4px] pr-[10px] text-[13px] text-[rgba(26,26,46,0.5)] dark:text-[var(--text-tertiary)]">Loading skills...</li>
                    ) : skills.length === 0 ? (
                      <li className="mobile-lessons-tree-skill-item relative py-[6px] pl-[4px] pr-[10px] text-[13px] text-[rgba(26,26,46,0.5)] dark:text-[var(--text-tertiary)]">No skills yet</li>
                    ) : (
                      skills.map((skill, skillIndex) => {
                        const skillId = skill.id || skill._id;
                        const skillTitle = skill.title || skill.name || skill['skill-name'] || `Skill ${skillId}`;
                        return (
                          <li key={skillId} className="mobile-lessons-tree-skill-item relative m-0 p-0">
                            <button
                              type="button"
                              className="flex items-center w-full py-2 pl-1 pr-[10px] bg-none border-none rounded-md text-[rgba(26,26,46,0.8)] dark:text-[var(--text-primary)] text-[14px] text-left cursor-pointer gap-2 hover:bg-[rgba(18,118,109,0.06)] dark:hover:bg-[var(--accent-teal-light)] hover:text-[rgba(26,26,46,0.95)] dark:hover:text-[var(--text-primary)]"
                              onClick={() => onSkillClick(skillId, lessonId)}
                              aria-label={`Open ${skillTitle}`}
                            >
                              <span className="mobile-lessons-tree-skill-alphabet w-[22px] h-[22px] rounded-full bg-[var(--progress-bg,rgba(18,118,109,0.08))] text-[var(--text-primary,rgba(26,26,46,0.9))] dark:bg-[var(--progress-bg)] dark:text-[var(--text-primary)] flex items-center justify-center text-[10px] font-semibold flex-shrink-0 border-2 border-[var(--text-tertiary,rgba(26,26,46,0.3))] dark:border-[var(--text-tertiary)]">
                                {getAlphabetLabel(skillIndex)}
                              </span>
                              <span className="mobile-lessons-tree-skill-title flex-1 min-w-0 break-words">{skillTitle}</span>
                              <span className="mobile-lessons-tree-skill-caret text-[16px] text-[rgba(26,26,46,0.3)] dark:text-[var(--text-tertiary)] flex-shrink-0">›</span>
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MobileLessonsTree;
